import React, { useState, useRef, useEffect } from 'react';
import { 
  MessageSquare,
  Search,
  Paperclip,
  HelpCircle,
  MoreVertical,
  Phone,
  MapPin,
  Home,
  Send
} from 'lucide-react';
import Layout from '../components/Layout';
import './Messages.css';

// Get initials from name
const getInitials = (name) => {
  if (!name || name === 'Unknown') return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();
};

// Generate a consistent color based on the name
const getAvatarColor = (name) => {
  const colors = [
    '#3B82F6', // blue
    '#10B981', // green
    '#F59E0B', // amber
    '#EF4444', // red
    '#8B5CF6', // purple
    '#EC4899', // pink
    '#06B6D4', // cyan
    '#F97316', // orange
  ];
  if (!name) return colors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Mock data for conversations
const mockConversations = [
  {
    id: '1',
    tenantName: 'Sarah Johnson',
    tenantPhone: '(555) 123-4567',
    propertyAddress: '123 Maple St, Apt 4B',
    lastMessage: 'The leak has been fixed, thank you!',
    timestamp: '2:30 PM',
    unread: 0,
  },
  {
    id: '2',
    tenantName: 'Michael Chen',
    tenantPhone: '(555) 234-5678',
    propertyAddress: '456 Oak Ave, Unit 2A',
    lastMessage: 'When can the maintenance team come?',
    timestamp: '1:15 PM',
    unread: 2,
  },
  {
    id: '3',
    tenantName: 'Emily Rodriguez',
    tenantPhone: '(555) 345-6789',
    propertyAddress: '789 Pine Rd, Suite 301',
    lastMessage: 'Here are the photos you requested',
    timestamp: '11:45 AM',
    unread: 1,
  },
  {
    id: '4',
    tenantName: 'David Kim',
    tenantPhone: '(555) 456-7890',
    propertyAddress: '321 Elm St, Apt 1C',
    lastMessage: 'Rent payment sent via portal',
    timestamp: 'Yesterday',
    unread: 0,
  },
  {
    id: '5',
    tenantName: 'Jessica Martinez',
    tenantPhone: '(555) 567-8901',
    propertyAddress: '654 Birch Ln, Unit 5D',
    lastMessage: 'Is it okay to paint the living room?',
    timestamp: 'Yesterday',
    unread: 0,
  },
];

// Mock messages for different conversations
const mockMessagesMap = {
  '1': [
    {
      id: 'm1',
      senderId: 'tenant',
      content: "Hi! There's a leak under the kitchen sink. Water is dripping constantly.",
      timestamp: '10:30 AM',
    },
    {
      id: 'm2',
      senderId: 'landlord',
      content: "Thanks for letting me know, Sarah. I'll send our maintenance team over this afternoon. Will you be home around 2 PM?",
      timestamp: '10:35 AM',
    },
    {
      id: 'm3',
      senderId: 'tenant',
      content: "Yes, I'll be here. Thank you!",
      timestamp: '10:37 AM',
    },
    {
      id: 'm4',
      senderId: 'landlord',
      content: 'Perfect. They should arrive between 2-3 PM.',
      timestamp: '10:40 AM',
    },
    {
      id: 'm5',
      senderId: 'tenant',
      content: 'The leak has been fixed, thank you!',
      timestamp: '2:30 PM',
    },
  ],
  '2': [
    {
      id: 'm1',
      senderId: 'tenant',
      content: "The heater in the bedroom isn't working properly. It's getting cold at night.",
      timestamp: '9:00 AM',
    },
    {
      id: 'm2',
      senderId: 'landlord',
      content: "I'm sorry to hear that, Michael. Let me check the schedule with our HVAC specialist.",
      timestamp: '9:15 AM',
    },
    {
      id: 'm3',
      senderId: 'tenant',
      content: "Here's a photo of the thermostat display",
      timestamp: '9:20 AM',
      attachment: {
        type: 'image',
        url: 'https://images.unsplash.com/photo-1661609860025-7ff8bdc05c10?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxhcGFydG1lbnQlMjBtYWludGVuYW5jZSUyMGlzc3VlfGVufDF8fHx8MTc2NzI1NjM1M3ww&ixlib=rb-4.1.0&q=80&w=400',
        name: 'thermostat.jpg',
      },
    },
    {
      id: 'm4',
      senderId: 'tenant',
      content: 'When can the maintenance team come?',
      timestamp: '1:15 PM',
    },
  ],
  '3': [
    {
      id: 'm1',
      senderId: 'landlord',
      content: 'Hi Emily, just checking in - how is everything at the apartment?',
      timestamp: '10:00 AM',
    },
    {
      id: 'm2',
      senderId: 'tenant',
      content: 'Everything is great! Just wanted to share - I noticed some minor scuff marks on the walls in the hallway. Should I be concerned?',
      timestamp: '10:15 AM',
    },
    {
      id: 'm3',
      senderId: 'landlord',
      content: 'Thanks for letting me know! Can you send me some photos so I can assess if we need to schedule a touch-up?',
      timestamp: '10:20 AM',
    },
    {
      id: 'm4',
      senderId: 'tenant',
      content: 'Here are the photos you requested',
      timestamp: '11:45 AM',
    },
  ],
};

// Conversation List Component
function ConversationList({ conversations, activeConversationId, onSelectConversation }) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredConversations = conversations.filter((conv) =>
    conv.tenantName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (conv.propertyAddress && conv.propertyAddress.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="conversation-list">
      <div className="conversation-list-header">
        <h2>Messages Demo</h2>
        <div className="search-container">
          <Search className="search-icon" size={18} />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="conversations">
        {filteredConversations.length === 0 ? (
          <div className="empty-conversations">
            <MessageSquare size={32} />
            <p>No conversations found</p>
          </div>
        ) : (
          filteredConversations.map((conversation) => (
            <div
              key={conversation.id}
              className={`conversation-item ${activeConversationId === conversation.id ? 'active' : ''}`}
              onClick={() => onSelectConversation(conversation.id)}
            >
              <div
                className="avatar"
                style={{ backgroundColor: getAvatarColor(conversation.tenantName) }}
              >
                {getInitials(conversation.tenantName)}
              </div>
              <div className="conversation-content">
                <div className="conversation-header">
                  <span className="tenant-name">{conversation.tenantName}</span>
                  <span className="timestamp">{conversation.timestamp}</span>
                </div>
                {conversation.propertyAddress && (
                  <div className="property-address">
                    <Home size={12} />
                    <span>{conversation.propertyAddress}</span>
                  </div>
                )}
                <div className="last-message-row">
                  <span className="last-message">{conversation.lastMessage}</span>
                  {conversation.unread > 0 && (
                    <span className="unread-badge">{conversation.unread}</span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Chat Window Component
function ChatWindow({ conversation, messages, onSendMessage }) {
  const [inputValue, setInputValue] = useState('');
  const [attachment, setAttachment] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() || attachment) {
      onSendMessage(inputValue.trim(), attachment);
      setInputValue('');
      setAttachment(null);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setAttachment(e.target.files[0]);
    }
  };

  if (!conversation) {
    return (
      <div className="chat-window empty">
        <div className="empty-state">
          <MessageSquare size={48} />
          <p>Select a conversation to start messaging</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-window">
      <div className="chat-header">
        <div
          className="avatar"
          style={{ backgroundColor: getAvatarColor(conversation.tenantName) }}
        >
          {getInitials(conversation.tenantName)}
        </div>
        <div className="header-info">
          <h3>{conversation.tenantName}</h3>
          <div className="header-details">
            {conversation.tenantPhone && (
              <span className="detail">
                <Phone size={14} />
                {conversation.tenantPhone}
              </span>
            )}
            {conversation.propertyAddress && (
              <span className="detail">
                <MapPin size={14} />
                {conversation.propertyAddress}
              </span>
            )}
          </div>
        </div>
        <button className="more-btn">
          <MoreVertical size={20} />
        </button>
      </div>

      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="no-messages">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`message ${message.senderId === 'landlord' ? 'sent' : 'received'}`}
            >
              <div className="message-bubble">
                {message.content}
                {message.attachment && (
                  <div className="message-attachment">
                    {message.attachment.type === 'image' ? (
                      <img src={message.attachment.url} alt={message.attachment.name} />
                    ) : (
                      <a href={message.attachment.url} target="_blank" rel="noopener noreferrer">
                        {message.attachment.name}
                      </a>
                    )}
                  </div>
                )}
              </div>
              <span className="message-timestamp">{message.timestamp}</span>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="message-input-container" onSubmit={handleSubmit}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          className="attach-btn"
          onClick={() => fileInputRef.current?.click()}
        >
          <Paperclip size={20} />
        </button>
        {attachment && (
          <span className="attachment-preview">
            {attachment.name}
            <button type="button" onClick={() => setAttachment(null)}>×</button>
          </span>
        )}
        <input
          type="text"
          placeholder="Type a message..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          className="message-input"
        />
        <button type="submit" className="send-btn" disabled={!inputValue.trim() && !attachment}>
          <Send size={20} />
        </button>
        <button type="button" className="help-btn">
          <HelpCircle size={20} />
        </button>
      </form>
    </div>
  );
}

// Main Messages Demo Page Component
export default function MessagesDemo() {
  const [activeConversationId, setActiveConversationId] = useState('1');
  const [conversations, setConversations] = useState(mockConversations);
  const [messagesMap, setMessagesMap] = useState(mockMessagesMap);

  const activeConversation = conversations.find((c) => c.id === activeConversationId) || null;
  const activeMessages = activeConversationId ? messagesMap[activeConversationId] || [] : [];

  const handleSendMessage = (content, attachment) => {
    if (!activeConversationId) return;

    const newMessage = {
      id: `m${Date.now()}`,
      senderId: 'landlord',
      content,
      timestamp: new Date().toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      }),
      attachment: attachment
        ? {
            type: attachment.type.startsWith('image/') ? 'image' : 'file',
            url: URL.createObjectURL(attachment),
            name: attachment.name,
          }
        : undefined,
    };

    setMessagesMap((prev) => ({
      ...prev,
      [activeConversationId]: [...(prev[activeConversationId] || []), newMessage],
    }));

    // Update last message in conversation list
    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === activeConversationId
          ? {
              ...conv,
              lastMessage: content || 'Sent an attachment',
              timestamp: 'Just now',
            }
          : conv
      )
    );
  };

  return (
    <Layout pageTitle="Messages Demo">
      <div className="messages-content">
        {/* Conversation List */}
        <ConversationList
          conversations={conversations}
          activeConversationId={activeConversationId}
          onSelectConversation={setActiveConversationId}
        />

        {/* Chat Window */}
        <ChatWindow
          conversation={activeConversation}
          messages={activeMessages}
          onSendMessage={handleSendMessage}
        />
      </div>
    </Layout>
  );
}
